import { Box, Checkbox, Tooltip } from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LegacyIndicatorIcon from 'pages/BuilderMode/icons/LegacyIndicatorIcon';
import BuilderModeV1IndicatorIcon from 'pages/BuilderModeV1/icons/BuilderModeV1IndicatorIcon';
import { useBuilderModeV1IndicatorIcons } from 'pages/BuilderModeV1/icons/BuilderModeV1IndicatorIconContext';
import { usePlanRevampTable } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/PlanRevampTableContext';
import { PLAN_DRIVER_TABLE_PLACEHOLDER } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planInputPanelTheme';
import { FORMULA_ERROR, CURRENCY_OPTIONS } from 'projectConstants';
import {
  getTableCheckBox,
  handleTableCheckBoxChange,
  handleTableCheckBoxChangeAll,
} from 'redux/BuilderModeSlice';

import { AppDispatch } from './GlobalHelpers';

interface IndicatorIconProperties {
  indicatorType: string | undefined;
}

interface TooltopProperties {
  tooltipText: string;
  text: string;
  placeholder: string;
  disabled?: boolean;
}

interface FormulaErrorBoxProperties {
  hasFormulaError: boolean;
  errorMessage?: any;
}

interface TableCheckBoxProperties {
  isHeading: boolean;
  itemId: number;
}

interface TooltipParagraphProperties {
  text: string;
  isNumber: boolean;
  isPlaceholder: boolean;
}

// Legacy (non-revamp) table cell look — untouched by anything the revamp
// tables need, so revamp-only styling changes can't reach it.
const LegacyTooltipParagraph = ({ text, isNumber }: TooltipParagraphProperties) => (
  <p style={{ width: '100%', paddingLeft: '.2rem', textAlign: isNumber ? 'right' : 'start' }}>
    {text}
  </p>
);

// Revamp-table cell look, split out so future revamp-only changes have their
// own component to land in instead of another isRevampTable branch inside
// the shared LegacyTooltipParagraph markup.
const RevampTooltipParagraph = ({ text, isNumber, isPlaceholder }: TooltipParagraphProperties) => (
  <p
    className={isPlaceholder ? 'revamp-table-placeholder' : undefined}
    style={{
      width: '100%',
      paddingLeft: 0,
      textAlign: isNumber ? 'right' : 'start',
      fontFamily: 'bold',
      fontSize: '14px',
      fontWeight: 700,
      color: isPlaceholder ? PLAN_DRIVER_TABLE_PLACEHOLDER : '#121826',
      margin: 0,
      lineHeight: 'normal',
    }}
  >
    {text}
  </p>
);

export const TooltipContent = ({ tooltipText, text, placeholder, disabled }: TooltopProperties) => {
  const isRevampTable = usePlanRevampTable();
  // Extract all unique currency symbols from CURRENCY_OPTIONS
  const currencySymbols = Array.from(new Set(CURRENCY_OPTIONS.map((option) => option.symbol)));
  // Combine with existing characters: %()-,.
  const allChars = [...currencySymbols, '%', '(', ')', ',', '.'];
  // Escape special regex characters for character class
  // In character class: - must be at end or escaped, ] \ ^ need escaping
  const escapedChars = allChars
    .filter((char) => char !== '-') // Remove - to add at end
    .map((char) => {
      if (char === ']') return '\\]';
      if (char === '\\') return '\\\\';
      if (char === '^') return '\\^';
      return char;
    });
  // Put - at the end (no escape needed when at end of character class)
  const pattern = `${escapedChars.join('')}-`;
  const cleanedText = text?.replace(new RegExp(`[${pattern}]`, 'g'), '').trim();
  // Check if it's a valid number and doesn't contain any alphabets
  const isNumber = /^-?\d+(\.\d+)?$/.test(cleanedText);
  const displayText = `${text}` || placeholder || '';
  const isPlaceholder = !text && Boolean(placeholder);

  const content = isRevampTable ? (
    <RevampTooltipParagraph text={displayText} isNumber={isNumber} isPlaceholder={isPlaceholder} />
  ) : (
    <LegacyTooltipParagraph text={displayText} isNumber={isNumber} isPlaceholder={isPlaceholder} />
  );

  return disabled ? content : <Tooltip label={tooltipText}>{content}</Tooltip>;
};

export const IndicatorIcon = ({ indicatorType }: IndicatorIconProperties) => {
  const useV1Icons = useBuilderModeV1IndicatorIcons();

  if (useV1Icons) {
    return <BuilderModeV1IndicatorIcon indicatorType={indicatorType} />;
  }

  return <LegacyIndicatorIcon indicatorType={indicatorType} />;
};

export const FormulaErrorBox = ({ hasFormulaError, errorMessage }: FormulaErrorBoxProperties) =>
  hasFormulaError ? (
    <Box ml={4} maxW={'max-content'} color={'red'} fontSize={'2xs'} position={'relative'}>
      <Tooltip label={errorMessage || FORMULA_ERROR} placement={'top'} variant={'primary'}>
        <InfoOutlineIcon height={5} width={5} />
      </Tooltip>
    </Box>
  ) : null;

export const TableCheckBox = ({ isHeading, itemId }: TableCheckBoxProperties) => {
  const dispatch: AppDispatch = useDispatch();
  const tableCheckBox: any = useSelector(getTableCheckBox);

  const allChecked = useMemo(() => {
    return tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 2]?.status;
  }, [tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 2]?.status]);

  const isIndeterminate = useMemo(() => {
    return tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 1]?.status;
  }, [tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 1]?.status]);

  const desiredObject = useMemo(
    () => tableCheckBox.find((object: any) => object.id === itemId),
    [tableCheckBox, itemId],
  );

  return (
    <>
      {isHeading ? (
        <Checkbox
          variant={'delete'}
          className='delete-all-checkbox'
          isChecked={allChecked}
          isIndeterminate={isIndeterminate}
          onChange={(event) => {
            dispatch(handleTableCheckBoxChangeAll({ status: event.target.checked }));
          }}
        ></Checkbox>
      ) : (
        <Checkbox
          variant={'delete'}
          isChecked={desiredObject?.status}
          onChange={(event) => {
            dispatch(handleTableCheckBoxChange({ itemId, status: event.target.checked }));
          }}
        ></Checkbox>
      )}
    </>
  );
};
